import React, { useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';

import { BakedBreadEntity } from 'src/firebase/db/BakedBreads';
import { BakedBreadTagEntity } from 'src/firebase/db/BakedBreadTags';
import { ShopEntity } from 'src/firebase/db/Shops';
import { State } from 'src/state/ducks';

import SelectBox from 'src/components/atoms/form/SelectBox';
import Button from 'src/components/atoms/button/Button';
import TextArea from 'src/components/atoms/form/TextArea';
import InputLabelHolder from 'src/components/molecules/form/InputLabelHolder';
import SingleInputFormBlock from 'src/components/molecules/form/SingleInputFormBlock';
import RadioInputBlock from 'src/components/molecules/form/RadioInputBlock';
import UploadImageBlock from 'src/components/organisms/form/UploadImageBlock';
import { ValidateMessageIfError } from 'src/components/atoms/form/ErrorMessage';

const mapEntityToState = (bakedBread: BakedBreadEntity) => {
  const { image, name, price, description, is_stopped, shop_id, baked_bread_tags } = bakedBread;

  return {
    image,
    name,
    price: price || (('' as unknown) as string),
    description,
    isStopped: is_stopped || false,
    shopId: shop_id,
    // 遷移のタイミングによっては undefined で落ちてしまうので... もっといい書き方があるはず 
    bakedBreadTagId: baked_bread_tags && baked_bread_tags.length && baked_bread_tags[0] ? baked_bread_tags[0].id : '',
  };
};

const mapStateToEntity = (
  state: ReturnType<typeof mapEntityToState>,
  bakedBreadTags: BakedBreadTagEntity[],
) => {
  return {
    image: state.image,
    name: state.name,
    price: +state.price,
    description: state.description,
    is_stopped: state.isStopped,
    shop_id: state.shopId,
    baked_bread_tags: bakedBreadTags.filter(({ id }) => id === state.bakedBreadTagId) || [],
  };
};

type Props = {
  formType: 'CREATE' | 'UPDATE';
  isAdmin: boolean;
  shops: ShopEntity[];
  bakedBreadTags: BakedBreadTagEntity[];
  bakedBread: BakedBreadEntity;
  errors: State['bakedBread']['validationErrors'];
  submit: (payload: BakedBreadEntity) => void;
};

const BakedBreadForm = (props: Props) => {
  const { shops, bakedBread, bakedBreadTags, errors, formType, isAdmin, submit } = props;
  const [state, setState] = useState(mapEntityToState(bakedBread));

  const shopOptions = useMemo(() => shops.map((shop) => ({ value: shop.id, content: shop.name })), [
    shops,
  ]);

  const bakedBreadTagOptions = useMemo(
    () => bakedBreadTags.map(({ id, name }) => ({ value: id, content: name })),
    [bakedBreadTags],
  );

  const canSubmit = [state.image, state.name, state.price, state.description].every((v) => v);
  const isNameEditable = isAdmin || formType === 'CREATE';

  const wrapOnSubmit = useCallback(() => {
    submit({
      ...bakedBread,
      ...mapStateToEntity(state, bakedBreadTags),
    });
  }, [submit, bakedBread, state, bakedBreadTags]);

  return (
    <div>
      <FormItem>
        <UploadImageBlock
          label="商品画像"
          thumbnails={state.image ? [state.image] : []}
          onChange={(images) =>
            setState((prev) => ({
              ...prev,
              image: images.find((v) => v) || '',
            }))
          }
          canUploadImageLength={1}
        />
        <ValidateMessageIfError marginVertical={8} messages={errors.image} />
      </FormItem>
      <FormItem>
        <InputLabelHolder labelName="対象店舗" isRequired />
        <SelectBox
          id="shop"
          placeholder=""
          inputValue={state.shopId}
          onChange={(shopId) =>
            setState((prev) => ({
              ...prev,
              shopId,
            }))
          }
          options={shopOptions}
        />
        <ValidateMessageIfError marginVertical={8} messages={errors.shop_id} />
      </FormItem>
      <FormItem>
        <SingleInputFormBlock
          isRequired={true}
          labelName={`商品名 ${!isNameEditable ? '（編集不可）' : ''}`}
          inputType="text"
          inputValue={state.name}
          placeholder=""
          onChange={(name) => setState((prev) => ({ ...prev, name }))}
          isError={false}
          isDisabeld={!isNameEditable}
        />
        <ValidateMessageIfError marginVertical={8} messages={errors.name} />
      </FormItem>
      <FormItem>
        <InputLabelHolder labelName="カテゴリー" isRequired />
        <SelectBox
          id="bakedBreadTag"
          placeholder=""
          inputValue={state.bakedBreadTagId}
          onChange={(bakedBreadTagId) => setState((prev) => ({ ...prev, bakedBreadTagId }))}
          options={bakedBreadTagOptions}
        />
        <ValidateMessageIfError marginVertical={8} messages={errors.baked_bread_tags} />
      </FormItem>
      <FormItem>
        <SingleInputFormBlock
          isRequired={true}
          labelName="販売単価（税抜）"
          inputType="text"
          inputValue={state.price}
          placeholder="0"
          onChange={(price) =>
            setState((prev) => {
              return { ...prev, price: price };
            })
          }
          isError={false}
          isDisabeld={false}
        />
        <ValidateMessageIfError marginVertical={8} messages={errors.price} />
      </FormItem>
      <FormItem>
        <InputLabelHolder labelName="商品説明" isRequired />
        <TextArea
          rows={10}
          max={280}
          inputValue={state.description}
          placeholder="商品の魅力を記入してください"
          onChange={(description) => setState((prev) => ({ ...prev, description }))}
          isError={false}
          isDisabeld={false}
        />
        <ValidateMessageIfError marginVertical={8} messages={errors.description} />
      </FormItem>
      <FormItem>
        <RadioInputBlock
          isRequired
          labelName="販売ステータス"
          id="sales_status"
          name="sales_status"
          value={state.isStopped ? '販売停止' : '販売中'}
          onValue="販売中"
          offValue="販売停止"
          onChange={(value) =>
            setState((prev) => ({
              ...prev,
              isStopped: value === '販売停止',
            }))
          }
        />
        <ValidateMessageIfError marginVertical={8} messages={errors.is_stopped} />
      </FormItem>
      <ButtonWrapper>
        <Button isDisabled={!canSubmit} clickEvent={() => wrapOnSubmit()} text="保存する" />
      </ButtonWrapper>
    </div>
  );
};

BakedBreadForm.defaultProps = {
  isAdmin: false,
};

const FormItem = styled.div`
  &:not(:first-child) {
    margin-top: 24px;
  }
`;
const ButtonWrapper = styled.div`
  margin-top: 36px;
`;

export default BakedBreadForm;
